import { Flex, Image, useTheme } from '@chakra-ui/react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';

import appConfig from '../../../config/app';
import apiClient from '../../api';
import { useCurrentColors } from '../../hooks/UseCurrentColors/UseCurrentColors';
import SpinnerOverlay from '../SpinnerOverlay/SpinnerOverlay';
import Activation from './Activation';
import ForgotPassword from './ForgotPassword';
import Login from './Login';
import ResetPassword from './ResetPassword';
import Verification from './Verification';
export interface LoginPageProps {
    api?: string;
    onLogin?: <T>(arg: T) => void;
    onSendResetLink?: <T>(arg: T) => void;
    userLabel?: string;
    passwordLabel?: string;
    logo?: string;
    backgroundImage?: string;
    t: any;
    setSuccess?: React.Dispatch<React.SetStateAction<string>>;
}

export type LoginFlow =
    | 'login'
    | 'forgotPassword'
    | '2fa_verification'
    | '2fa_activation'
    | 'logged_in'
    | 'reset_password';

const usePrevious = <T extends unknown>(value: T): T | undefined => {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};
const LoginPage: React.FC<LoginPageProps> = (props) => {
    const currentColors = useCurrentColors();
    const theme = useTheme();
    const { logo = theme?.images?.loginLogo, t } = props;

    const [loginFlow, setLoginFlow] = useState<LoginFlow>('login');
    const [loginResponse, setLoginResponse] = useState<Record<string, unknown>>(
        {}
    );
    const [success, setSuccess] = useState('');
    const prevLoginFlow = usePrevious(loginFlow);

    const loginFlowComponentMap: Record<LoginFlow, () => JSX.Element> = {
        forgotPassword: () => (
            <ForgotPassword
                {...props}
                setLoginFlow={setLoginFlow}></ForgotPassword>
        ),
        login: () => (
            <Login
                {...props}
                success={success}
                setLoginFlow={setLoginFlow}
                setLoginResponse={setLoginResponse}></Login>
        ),
        '2fa_verification': () => (
            <Verification
                {...props}
                setLoginResponse={(response: any) => {
                    setLoginResponse((loginResponse: any) => {
                        return {
                            ...response,
                            password: loginResponse?.password,
                        };
                    });
                }}
                setLoginFlow={setLoginFlow}></Verification>
        ),
        '2fa_activation': () => (
            <Activation
                user={loginResponse}
                setLoginResponse={(response: any) => {
                    setLoginResponse((loginResponse: any) => {
                        return {
                            ...response,
                            password: loginResponse?.password,
                        };
                    });
                }}
                setLoginFlow={setLoginFlow}></Activation>
        ),
        reset_password: () => (
            <ResetPassword
                {...props}
                setSuccess={setSuccess}
                user={loginResponse}
                setLoginFlow={setLoginFlow}></ResetPassword>
        ),
        logged_in: () => {
            return <SpinnerOverlay text={t('loading')}></SpinnerOverlay>;
        },
    };
    useEffect(() => {
        if (loginFlow === 'logged_in') {
            let user: any = apiClient.session;
            if (prevLoginFlow === 'reset_password') {
                user = loginResponse;
            }
            delete user.password;
            props?.onLogin && props.onLogin(user);
        }
    }, [loginFlow]);

    useEffect(() => {
        if (appConfig?.basePath) {
            window.location.href = '/';
            return;
        }
    }, []);

    if (loginFlow === 'logged_in') {
        return <SpinnerOverlay text={t('loading')}></SpinnerOverlay>;
    }

    return (
        <Flex
            position="relative"
            width="100%"
            minHeight="100vh"
            align="center"
            direction="column"
            backgroundImage={`url('${theme?.images?.loginBg}')`}
            backgroundPosition="center"
            backgroundSize="cover"
            backgroundRepeat="no-repeat">
            <Flex
                maxWidth={['318px', '384px', '470px', '470px', '470px']}
                width={{ xl: '100%', sm: 'calc(100% - 22px)' }}
                direction="column"
                minHeight="495px"
                backgroundColor={currentColors.componentBg}
                boxShadow={`1px 2px 6px ${currentColors.boxShadow}`}
                border="1px"
                borderColor={currentColors.componentBorder}
                borderRadius="6px"
                align="center"
                m="0"
                mt="100px"
                mb="20px"
                pr="25px"
                pl={{ sm: '19px', xl: '25px' }}
                py="30px">
                <Image
                    src={logo}
                    width="auto"
                    maxWidth="100%"
                    height="auto"
                    maxHeight="90px"
                />
                {loginFlowComponentMap[loginFlow]()}
            </Flex>
            {theme?.isMainline ? (
                <Flex
                    fontSize="13px"
                    color={currentColors.white}
                    flexGrow={1}
                    alignItems="flex-end">
                    <Flex
                        mb="40px"
                        mt="40px">
                        Copyright &copy; {moment().format('YYYY')} Canopy Pte.
                        Ltd.
                    </Flex>
                </Flex>
            ) : null}
        </Flex>
    );
};

LoginPage.propTypes = {
    t: PropTypes.func.isRequired,
};

export default withTranslation('translations')(LoginPage);
